import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchListAttendees, setListAttendeesPage } from '@/store/slices/audiences';
import {
  selectListAttendeesFields,
  selectListAttendeesPage,
  selectListAttendeesPageSize,
  selectListAttendeesSearch,
  selectListAttendeesSort,
  selectListAttendeesSortDirection,
} from '@/store/slices/audiences/selectors';
import { selectWorkingProjectUuid } from '@/store/slices/project/selectors';
import { selectActiveChannel } from '@/store/slices/user/selectors';
import type { RegistrationFilter } from '@/types/all-attendees.types';

export const useLoadListAttendees = (passcodeListId: number, registrationStatusFilter?: RegistrationFilter) => {
  const dispatch = useAppDispatch();
  const channel = useAppSelector(selectActiveChannel);
  const eventUuid = useAppSelector(selectWorkingProjectUuid);
  const page = useAppSelector(selectListAttendeesPage);
  const pageSize = useAppSelector(selectListAttendeesPageSize);
  const search = useAppSelector(selectListAttendeesSearch);
  const sort = useAppSelector(selectListAttendeesSort);
  const sortDirection = useAppSelector(selectListAttendeesSortDirection);
  const fields = useAppSelector(selectListAttendeesFields);

  // Both filters come from the URL (the registrationStatus search param), so they can change
  // without a remount. A filter change invalidates the current page: reset to page 1 and skip
  // this fetch — the page update re-runs the effect, which then fetches once with the new filter.
  const previousFilter = useRef(registrationStatusFilter);

  useEffect(() => {
    if (!eventUuid || !passcodeListId) return;

    const filterChanged = previousFilter.current !== registrationStatusFilter;
    previousFilter.current = registrationStatusFilter;

    if (filterChanged && page !== 1) {
      dispatch(setListAttendeesPage(1));
      return;
    }

    dispatch(fetchListAttendees({ passcodeListId, registrationStatus: registrationStatusFilter }));
  }, [
    channel,
    eventUuid,
    passcodeListId,
    page,
    pageSize,
    search,
    sort,
    sortDirection,
    fields,
    registrationStatusFilter,
    dispatch,
  ]);
};
